import { ButtonHTMLAttributes, FC, forwardRef } from 'react';
import { cn, FaIcon, Spinner } from '@fe-learner-common';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  startIcon?: IconDefinition;
  endIcon?: IconDefinition;
  loading?: boolean;
  size: 'sm' | 'md' | 'lg';
  ariaLabel?: string;
}

const sizeMap = {
  sm: 'lf:l-btn-sm',
  md: 'lf:l-btn-md',
  lg: 'lf:l-btn-lg',
};

const Icon = ({ icon }: { icon: IconDefinition }) => (
  <FaIcon
    className="lf:aspect-square lf:text-inherit"
    icon={icon}
    width={16}
    height={16}
  />
);

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { children, className, loading, name, size, startIcon, endIcon, ...rest },
    ref,
  ) => {
    return (
      <button
        {...rest}
        ref={ref}
        disabled={loading}
        className={cn('lf:l-btn', sizeMap[size], className)}
      >
        {startIcon && <Icon icon={startIcon} />}
        {loading ? <Spinner name={name} /> : children}
        {endIcon && <Icon icon={endIcon} />}
      </button>
    );
  },
);

Button.displayName = 'Button';

export const PrimaryButton: FC<ButtonProps> = forwardRef<
  HTMLButtonElement,
  ButtonProps
>(({ children, className, ...rest }, ref) => {
  const extendedClassName = cn(
    'lf:l-btn-primary',
    { 'lf:bg-theme-colors-primary-dark': rest.loading },
    className,
  );

  return (
    <Button
      ref={ref}
      name="PrimaryButton"
      className={extendedClassName}
      {...rest}
    >
      {children}
    </Button>
  );
});

PrimaryButton.displayName = 'PrimaryButton';

export const SecondaryButton: FC<ButtonProps> = forwardRef<
  HTMLButtonElement,
  ButtonProps
>(({ children, className, ...rest }, ref) => {
  const extendedClassName = cn(
    'lf:l-btn-secondary',
    { 'lf:bg-neutral-200': rest.loading },
    className,
  );

  return (
    <Button ref={ref} className={extendedClassName} {...rest}>
      {children}
    </Button>
  );
});
SecondaryButton.displayName = 'SecondaryButton';

export const TertiaryButton: FC<ButtonProps> = forwardRef<
  HTMLButtonElement,
  ButtonProps
>(({ children, className, ...rest }, ref) => {
  const extendedClassName = cn(
    'lf:l-btn-tertiary',
    { 'lf:bg-neutral-100': rest.loading },
    className,
  );

  return (
    <Button ref={ref} className={extendedClassName} {...rest}>
      {children}
    </Button>
  );
});

TertiaryButton.displayName = 'TertiaryButton';
